// src/components/common/Header.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../hooks/useNotifications';
import GlobalSearch from './GlobalSearch';
import LanguageSwitcher from './LanguageSwitcher';
import {
  Bars3Icon, BellIcon, ArrowRightOnRectangleIcon,
  Cog6ToothIcon, UserIcon,
} from '@heroicons/react/24/outline';
import { PLANS } from '../../utils/constants';
import { useTranslation } from 'react-i18next';

const PLAN_BADGE = {
  [PLANS.FREE]: { label: 'FREE', cls: 'bg-gray-100 text-gray-600' },
  [PLANS.STARTER]: { label: 'STARTER', cls: 'bg-blue-100 text-blue-700' },
  [PLANS.PRO]: { label: 'PRO', cls: 'bg-purple-100 text-purple-700' },
  [PLANS.ENTERPRISE]: { label: 'ENT', cls: 'bg-yellow-100 text-yellow-800' },
};

const Header = ({ toggleSidebar }) => {
  const { t } = useTranslation();
  const { user, organization, effectivePlan, isSuperAdmin, logout } = useAuth();
  const { notifications, unreadCount, markAllRead } = useNotifications();
  const navigate = useNavigate();

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const menuRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowUserMenu(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifications(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const badge = PLAN_BADGE[effectivePlan] || { label: effectivePlan || '...', cls: 'bg-gray-100 text-gray-700' };
  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
      <div className="px-4 sm:px-6">
        <div className="flex justify-between items-center h-16 gap-4">

          {/* Sinistra */}
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={toggleSidebar}
              className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition flex-shrink-0">
              <Bars3Icon className="h-5 w-5" />
            </button>
            <div className="hidden lg:flex items-center gap-1.5 min-w-0">
              <span className="text-gray-300">/</span>
              <span className="text-sm font-medium text-gray-600 truncate max-w-xs">
                {organization?.name || 'Dashboard'}
              </span>
            </div>
          </div>

          {/* Centro — ricerca */}
          <div className="flex-1 flex justify-center max-w-sm">
            <GlobalSearch />
          </div>

          {/* Destra */}
          <div className="flex items-center gap-2 flex-shrink-0">

            {/* Lingua */}
            <LanguageSwitcher compact={true} />

            {/* Badge piano */}
            <span className={`hidden sm:inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full ${badge.cls}`}>
              {isSuperAdmin && <span className="mr-1">👑</span>}
              {badge.label}
            </span>

            {/* Notifiche */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition">
                <BellIcon className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 border-2 border-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
                  <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900">
                      Notifiche {unreadCount > 0 && <span className="text-blue-600">({unreadCount})</span>}
                    </p>
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} className="text-xs text-blue-600 hover:underline">
                        Segna tutte lette
                      </button>
                    )}
                  </div>
                  <div className="divide-y divide-gray-50 max-h-72 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-gray-400">
                        Nessuna notifica
                      </div>
                    ) : notifications.slice(0, 5).map(n => (
                      <div key={n.id} className={`px-4 py-3 hover:bg-gray-50 transition cursor-pointer ${!n.read ? 'bg-blue-50/40' : ''}`}>
                        <div className="flex gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-xl flex items-center justify-center text-sm flex-shrink-0">
                            {n.type === 'budget_alert' ? '📊' : n.type === 'expense_validated' ? '✅' : '🔔'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{n.title}</p>
                            <p className="text-xs text-gray-400 truncate">{n.message}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="px-4 py-2.5 border-t bg-gray-50 text-center">
                    <Link to="/notifications" onClick={() => setShowNotifications(false)}
                      className="text-xs text-blue-600 hover:underline font-medium">
                      Vedi tutte le notifiche
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Profilo */}
            <div className="relative" ref={menuRef}>
              <button onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2.5 pl-1 pr-2 py-1 rounded-xl hover:bg-gray-100 transition">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                  {initials}
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-xs font-semibold text-gray-800 leading-tight">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-gray-400 leading-tight">{user?.role}</p>
                </div>
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
                  <div className="px-4 py-3 bg-gray-50 border-b">
                    <p className="text-xs font-semibold text-gray-900">{user?.firstName} {user?.lastName}</p>
                    <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                  </div>
                  <div className="p-1.5 space-y-0.5">
                    <Link to="/profile" onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-xl transition">
                      <UserIcon className="h-4 w-4 text-gray-400" /> Profilo
                    </Link>
                    <Link to="/settings" onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-xl transition">
                      <Cog6ToothIcon className="h-4 w-4 text-gray-400" /> Impostazioni
                    </Link>
                    {isSuperAdmin && (
                      <Link to="/superadmin" onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-2.5 px-3 py-2 text-sm text-yellow-700 hover:bg-yellow-50 rounded-xl transition">
                        <span>👑</span> SuperAdmin Panel
                      </Link>
                    )}
                  </div>
                  <div className="p-1.5 border-t border-gray-100">
                    <button onClick={handleLogout}
                      className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-xl transition">
                      <ArrowRightOnRectangleIcon className="h-4 w-4" /> Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;